// Day types per date (Ziek, Vakantie, Vrij, any type) and the ritme editor (spec R1 to R4).
// Rebuilding days is the server's job (L35): the device records the change and shows the new day after the sync.

import { useLiveQuery } from "dexie-react-hooks";
import { pausedDays, pickTemplateId } from "@shared/rhythm";
import type { MutationArgs } from "@shared/sync";
import { uuidv7 } from "@shared/uuid";
import { db, getMeta, type DayOverride, type DayTemplate, type DayTemplateBlock, type DayTemplateRule } from "./db";
import type { Me } from "./session";
import { sync } from "./sync";

const now = () => new Date().toISOString();

export interface Rhythm {
  templates: DayTemplate[];
  rules: DayTemplateRule[];
  overrides: DayOverride[];
  blocks: DayTemplateBlock[];
}

/** Everything about the rhythm, live: day types (not archived), their blocks, weekday rules and exceptions. */
export function useRhythm(): Rhythm | undefined {
  return useLiveQuery(async () => {
    const [templates, rules, overrides, blocks] = await Promise.all([
      db.day_templates.toArray(),
      db.day_template_rules.toArray(),
      db.day_overrides.toArray(),
      db.day_template_blocks.toArray(),
    ]);
    return {
      templates: templates.filter((t) => !t.deleted_at && !t.archived_at).sort((a, b) => a.position - b.position),
      rules: rules.filter((r) => !r.deleted_at),
      overrides: overrides.filter((o) => !o.deleted_at),
      blocks: blocks.filter((b) => !b.deleted_at),
    };
  });
}

/** Is a date a Ziek or Vakantie day (no routines, lower week goal)? */
export function isPausedIn(rhythm: Rhythm | undefined): (date: string) => boolean {
  if (!rhythm) return () => false;
  return pausedDays(rhythm.templates, rhythm.rules, rhythm.overrides);
}

/** The day type a date has by its weekday alone, without exceptions ("Normaal"). */
export function standardTemplate(rhythm: Rhythm, date: string): DayTemplate | undefined {
  const id = pickTemplateId(date, rhythm.templates, rhythm.rules, []);
  return rhythm.templates.find((t) => t.id === id);
}

/** The exception covering a date, if any. */
export function overrideOn(rhythm: Rhythm, date: string): DayOverride | undefined {
  return rhythm.overrides.find((o) => o.starts_on <= date && o.ends_on >= date);
}

/** Another day type for from..to. Returns the exception id (for undo). */
export async function setDayType(from: string, to: string, templateId: string): Promise<string> {
  const id = uuidv7();
  const at = now();
  const me = await getMeta<Me>("me");
  await sync.mutate("day.setType", { id, from, to, templateId, at }, [id], async () => {
    await db.day_overrides.add({
      id,
      space_id: me?.settings?.personal_space_id ?? "",
      user_id: me?.user.id ?? "",
      starts_on: from,
      ends_on: to,
      template_id: templateId,
      reason: null,
      unplanned_entry_ids: [],
      replaced: [],
      split_ids: [],
      version: 0,
      deleted_at: null,
      created_at: at,
      updated_at: at,
    });
  });
  return id;
}

/** Back to the normal day type for from..to; with undoId it undoes that exception and its consequences. */
export async function clearDayType(from: string, to: string, undoId?: string): Promise<void> {
  const at = now();
  const touched = (await db.day_overrides.toArray()).filter((o) => !o.deleted_at && o.starts_on <= to && o.ends_on >= from);
  await sync.mutate(
    "day.clearType",
    { from, to, at, ...(undoId ? { undoId } : {}) },
    touched.map((o) => o.id),
    async () => {
      // Locally only exceptions fully inside the range go; the server trims the rest and sends it back.
      for (const o of touched) if (o.starts_on >= from && o.ends_on <= to) await db.day_overrides.update(o.id, { deleted_at: at });
    },
  );
}

export async function saveTemplate(id: string | null, name: string, weekdays: number[]): Promise<string> {
  const templateId = id ?? uuidv7();
  const me = await getMeta<Me>("me");
  const at = now();
  await sync.mutate("template.save", { id: templateId, name: name.trim(), weekdays }, [templateId], async () => {
    if (id) {
      await db.day_templates.update(id, { name: name.trim() });
    } else {
      const position = Math.max(-1, ...(await db.day_templates.toArray()).map((t) => t.position)) + 1;
      await db.day_templates.add({
        id: templateId,
        space_id: me?.settings?.personal_space_id ?? "",
        user_id: me?.user.id ?? "",
        name: name.trim(),
        is_fallback: false,
        position,
        archived_at: null,
        pauses_routines: false,
        seed_key: null,
        morning_start: null,
        version: 0,
        deleted_at: null,
        created_at: at,
        updated_at: at,
      });
    }
  });
  return templateId;
}

export async function duplicateTemplate(id: string, name: string): Promise<string> {
  const newId = uuidv7();
  const source = await db.day_templates.get(id);
  const at = now();
  // The copy is here at once; its blocks are made by the server and arrive with the sync (one place for the copy rule).
  await sync.mutate("template.duplicate", { id, newId, name: name.trim() }, [newId], async () => {
    if (!source) return;
    const position = Math.max(-1, ...(await db.day_templates.toArray()).map((t) => t.position)) + 1;
    await db.day_templates.add({
      ...source,
      id: newId,
      name: name.trim(),
      is_fallback: false,
      seed_key: null,
      position,
      version: 0,
      created_at: at,
      updated_at: at,
    });
  });
  return newId;
}

export async function archiveTemplate(id: string): Promise<void> {
  const at = now();
  await sync.mutate("template.archive", { id, at }, [id], async () => {
    await db.day_templates.update(id, { archived_at: at });
  });
}

export type BlockForm = Omit<MutationArgs<"templateBlock.save">, "id" | "templateId">;

export async function saveTemplateBlock(templateId: string, id: string | null, form: BlockForm): Promise<string> {
  const blockId = id ?? uuidv7();
  const template = await db.day_templates.get(templateId);
  const at = now();
  const fields = {
    title: form.title.trim(),
    slot_kind: form.slotKind,
    start_local: `${form.startLocal}:00`,
    end_local: form.endLocal ? `${form.endLocal}:00` : null,
    group_title: form.groupTitle,
    is_optional: form.isOptional,
    is_fixed: form.isFixed || form.slotKind === "work",
    notify: form.notify,
  };
  await sync.mutate("templateBlock.save", { id: blockId, templateId, ...form, title: fields.title }, [blockId], async () => {
    if (id) {
      await db.day_template_blocks.update(id, fields);
    } else {
      await db.day_template_blocks.add({
        id: blockId,
        template_id: templateId,
        space_id: template?.space_id ?? "",
        ...fields,
        lead_minutes: 0,
        external_url: null,
        position: 0,
        version: 0,
        deleted_at: null,
        created_at: at,
        updated_at: at,
      });
    }
  });
  return blockId;
}

export async function removeTemplateBlock(id: string): Promise<void> {
  const at = now();
  await sync.mutate("templateBlock.remove", { id, at }, [id], async () => {
    await db.day_template_blocks.update(id, { deleted_at: at });
  });
}

/** "Ook morgen en verder": days that exist from `from` follow the rhythm as it is now (R2 AC2). */
export async function applyTemplate(templateId: string, from: string): Promise<void> {
  await sync.mutate("template.apply", { templateId, from }, [], async () => {});
}
